import type {
  ClaimElement,
  ConceptElement,
  DomainElement,
  FeatureElement,
  PaperElement,
  PhenomenonElement,
  RegionLabel,
  SystemElement,
} from './types';

/**
 * Spatial coordinates are authored, not algorithmic.
 * x = lateral, y = vertical, z = depth (negative = away from camera).
 *
 * Composition:
 *   • Unification field (feature + 4 domains) — center, slightly elevated
 *   • Foundational concepts — high band, behind the field
 *   • Formal apparatus — right wing, mid-elevation
 *   • Diagnostic concepts — left wing, mid-elevation
 *   • Derived concepts — back-center, quieter
 *   • Claims — upper arc, propositional band
 *   • Phenomena — lower outer ring (what the system addresses)
 *   • Papers — far flanks (the formal record)
 */

export const FEATURE: FeatureElement = {
  id: 'feature',
  kind: 'feature',
  index: '∗',
  title: 'The Structural Feature',
  subtitle: 'A bounded synthesis',
  blurb:
    'A single underlying feature unifying selection in QM, inhabitation in modal metaphysics, agency-biasing in free will, and the disclosive cut in mind.',
  body: [
    'Across four apparently distinct fields, philosophy has confronted what looks like four distinct impasses: how a measurement outcome is selected from a superposition; how one possible world is inhabited rather than merely possible; how an agent originates an action without infinite regress; how phenomenal experience relates to its physical substrate.',
    'The claim of this system is that these are aspects of one structural feature — a bounded synthesis at which the conditions of intelligibility coincide with the conditions of agency. Each field has been gesturing at the same edge from a different side.',
    'The Structural Feature is not an entity. It is the place where four explanatory regresses come to rest in the same way.',
  ],
  related: [
    'qm-domain',
    'modal-domain',
    'mind-domain',
    'freewill-domain',
    'freedom',
    'worldframe',
    'disclosive-cut',
  ],
  position: [0, 0.6, 0],
};

export const DOMAINS: DomainElement[] = [
  {
    id: 'qm-domain',
    kind: 'domain',
    index: 'I',
    field: 'qm',
    title: 'Quantum Mechanics',
    blurb: 'How is one outcome selected from a superposition?',
    body: [
      'The quantum measurement problem asks how the unitary evolution of a superposed state gives way to a single, definite outcome. Standard interpretations distribute the cost across ontology, locality, or determinacy — each preserving some commitments at the price of others.',
    ],
    positions: ['Copenhagen — observer-relative collapse', 'Many-Worlds — branching without selection', 'Bohmian — hidden variables, non-locality'],
    related: ['feature', 'measurement-problem', 'quantum-eraser', 'wavefunction-resonance', 'quantum-worldframe'],
    position: [-4.6, -0.4, 1.2],
  },
  {
    id: 'modal-domain',
    kind: 'domain',
    index: 'II',
    field: 'modal',
    title: 'Modal Metaphysics',
    blurb: 'What makes one possible world the actual one?',
    body: [
      'Modal realism takes possible worlds as fully real but yields no principled answer for why this world is inhabited. Ersatzism reduces possibility to representation but loses the bite of genuine modal claims.',
    ],
    positions: ['Lewisian Realism — all worlds equally real', 'Ersatzism — possibilities as abstracta', 'Actualism — only the actual'],
    related: ['feature', 'modal-realism-indeterminacy', 'participatory-universe', 'monad', 'monadic-inhabitation', 'inhabitation-constitutive'],
    position: [4.6, -0.4, 1.2],
  },
  {
    id: 'mind-domain',
    kind: 'domain',
    index: 'III',
    field: 'mind',
    title: 'Philosophy of Mind',
    blurb: 'Why does any of this feel like something?',
    body: [
      'The hard problem and the explanatory gap mark a point at which structural description appears insufficient. Eliminativism denies the explanandum; panpsychism multiplies it; illusionism re-describes it.',
    ],
    positions: ['Physicalism — phenomenal reduces to physical', 'Panpsychism — phenomenal is fundamental', 'Illusionism — phenomenal is artifact'],
    related: ['feature', 'hard-problem', 'marys-room', 'disclosive-cut', 'disclosive-hard-problem'],
    position: [-2.9, -0.4, 4.2],
  },
  {
    id: 'freewill-domain',
    kind: 'domain',
    index: 'IV',
    field: 'freewill',
    title: 'Free Will',
    blurb: 'Can an action be genuinely up to its agent?',
    body: [
      'Compatibilism preserves moral practice at the cost of metaphysical bite; libertarianism preserves origination at the cost of intelligibility; hard determinism dissolves the question. The literature has settled into trench warfare around what an "origination" could even be.',
    ],
    positions: ['Compatibilism — freedom as responsiveness', 'Libertarianism — agent as uncaused cause', 'Hard determinism — no genuine freedom'],
    related: ['feature', 'libertarian-compatibilist', 'three-layer-causa-sui', 'conditions-of-agency', 'agency-causa-sui'],
    position: [2.9, -0.4, 4.2],
  },
];

export const CONCEPTS: ConceptElement[] = [
  // Foundational (4)
  {
    id: 'freedom',
    kind: 'concept',
    group: 'foundational',
    index: 'C·01',
    title: 'Freedom',
    subtitle: 'The sole fundamental reality',
    role: 'Ground',
    blurb:
      'Not a property of agents but the condition under which there is anything at all to be an agent of.',
    body: [
      'Freedom, in this system, is not the libertarian "could have done otherwise." It is the unfounded openness from which any determinate state takes its determinacy. Necessity, possibility, and actuality are derivative — modes of how freedom takes hold.',
      'To call freedom fundamental is to refuse the demand for an underlying mechanism. The demand itself presupposes what it would explain.',
    ],
    related: ['worldframe', 'monad', 'conditions-of-agency', 'three-layer-causa-sui', 'freedom-fundamental'],
    position: [-3.6, 4.3, -3.6],
  },
  {
    id: 'worldframe',
    kind: 'concept',
    group: 'foundational',
    index: 'C·02',
    title: 'Worldframe',
    subtitle: 'The unit of inhabited reality',
    role: 'Ground',
    blurb:
      'Not a possible world among many, but the framing within which questions of actuality and possibility can be raised at all.',
    body: [
      'A worldframe is what is opened by an inhabitant. It is not selected from a Lewisian plenum; it is what shows up because something is there to undergo it. Two worldframes do not stand in a contrastive relation — there is no neutral ground from which to compare them.',
    ],
    related: ['monad', 'monadic-inhabitation', 'conditions-of-intelligibility', 'inhabitation-constitutive', 'quantum-worldframe', 'r-monad'],
    position: [-1.2, 4.6, -3.6],
  },
  {
    id: 'monad',
    kind: 'concept',
    group: 'foundational',
    index: 'C·03',
    title: 'Monad',
    subtitle: 'The inhabitant of a worldframe',
    role: 'Ground',
    blurb:
      'A bounded perspective whose having-of-the-world is what makes the world a world.',
    body: [
      'The monad here is not Leibnizian (windowless, mirroring the whole) but a more austere structural item: that for which a worldframe is the case. Every worldframe is monadic; no worldframe is uninhabited.',
    ],
    related: ['worldframe', 'r-monad', 'monadic-inhabitation', 'modal-domain'],
    position: [1.2, 4.6, -3.6],
  },
  {
    id: 'disclosive-cut',
    kind: 'concept',
    group: 'foundational',
    index: 'C·04',
    title: 'The Disclosive / Structural Distinction',
    subtitle: 'A cut, not a gap',
    role: 'Ground',
    blurb:
      'The phenomenal and the structural are not levels of the same thing — they are two registers in which one event is articulated.',
    body: [
      'The "hard problem" arises when the phenomenal is conceived as something extra that must be added to a complete structural description. The disclosive/structural distinction reframes: structural description is one register of an event; disclosure is the other. Neither is reducible to the other; neither is a residue.',
    ],
    related: ['mind-domain', 'hard-problem', 'marys-room', 'disclosive-hard-problem', 'paper-disclosive'],
    position: [3.6, 4.3, -3.6],
  },

  // Formal apparatus (3)
  {
    id: 'r-monad',
    kind: 'concept',
    group: 'formal',
    index: 'F·01',
    title: 'R-Monad',
    subtitle: 'Resonant monad',
    role: 'Apparatus',
    blurb:
      'A monad characterised by its resonance pattern — the structure of compatibility with which a worldframe can be undergone.',
    body: [
      'The R-monad gives the formal handle on what otherwise remains slogan. A resonance pattern is not a state space but a profile of admissibility: which articulations of a worldframe a monad can sustain.',
    ],
    related: ['monad', 'wavefunction-resonance', 'coherence-functional'],
    position: [7.6, 2.5, -1.0],
  },
  {
    id: 'coherence-functional',
    kind: 'concept',
    group: 'formal',
    index: 'F·02',
    title: 'Coherence Functional',
    subtitle: 'The admissibility measure',
    role: 'Apparatus',
    blurb:
      'A functional whose stationary points correspond to inhabitable worldframes.',
    body: [
      'Where conventional formalism asks for the dynamics that produce a state, the coherence functional asks for what makes a state inhabitable in the first place. Its stationary points are the worldframes that can be the case.',
    ],
    related: ['r-monad', 'wavefunction-resonance', 'conditions-of-intelligibility'],
    position: [8.6, 0.9, 1.6],
  },
  {
    id: 'wavefunction-resonance',
    kind: 'concept',
    group: 'formal',
    index: 'F·03',
    title: 'Wavefunction as Modal Resonance',
    subtitle: 'Reinterpreting Ψ',
    role: 'Apparatus',
    blurb:
      'The quantum wavefunction as the admissibility profile of a worldframe under candidate articulations.',
    body: [
      'Read structurally: amplitudes are not propensities to occur, but compatibilities of an inhabitable worldframe with a candidate articulation. Selection is then not a collapse but a coming-into-coincidence of monad and articulation.',
    ],
    related: ['qm-domain', 'measurement-problem', 'quantum-worldframe', 'r-monad', 'paper-modal-resonance'],
    position: [7.0, -0.6, 4.0],
  },

  // Diagnostic (4)
  {
    id: 'conditions-of-intelligibility',
    kind: 'concept',
    group: 'diagnostic',
    index: 'D·01',
    title: 'Conditions of Intelligibility',
    subtitle: 'What lets a world show up as a world',
    role: 'Diagnosis',
    blurb:
      'The structural prerequisites under which articulations have purchase rather than running idle.',
    body: [
      'Conditions of intelligibility are not epistemic. They are what must hold for there to be something for cognition to be of. The diagnostic move is to show, in each domain, that its impasse traces to a condition of intelligibility being treated as an explanandum.',
    ],
    related: ['worldframe', 'coherence-functional', 'conditions-of-agency', 'paper-worldframes'],
    position: [-7.6, 2.5, -1.0],
  },
  {
    id: 'conditions-of-agency',
    kind: 'concept',
    group: 'diagnostic',
    index: 'D·02',
    title: 'Conditions of Agency',
    subtitle: 'What lets an action originate',
    role: 'Diagnosis',
    blurb:
      'The structural prerequisites under which a doing is the agent\'s rather than merely a happening.',
    body: [
      'Conditions of agency parallel conditions of intelligibility but in the modality of doing rather than showing. The free-will impasse arises from looking for an agent-cause where what is needed is the structure that makes origination intelligible.',
    ],
    related: ['freedom', 'three-layer-causa-sui', 'libertarian-compatibilist', 'agency-causa-sui'],
    position: [-8.6, 0.9, 1.6],
  },
  {
    id: 'three-layer-causa-sui',
    kind: 'concept',
    group: 'diagnostic',
    index: 'D·03',
    title: 'Three-Layer Causa Sui',
    subtitle: 'A bounded self-causation',
    role: 'Diagnosis',
    blurb:
      'Genuine agency requires the agent to be cause of itself only in a structurally bounded way — three layers, none viciously circular.',
    body: [
      'The classical causa sui is rejected as logically impossible. The three-layer version: an agent originates an action by reflexively shaping the conditions under which the agent that originates is itself constituted, but not the conditions on which that constitution depends.',
    ],
    related: ['freedom', 'conditions-of-agency', 'agency-causa-sui', 'freewill-domain'],
    position: [-7.0, -0.6, 4.0],
  },
  {
    id: 'carried-incoherence',
    kind: 'concept',
    group: 'diagnostic',
    index: 'D·04',
    title: 'Carried Incoherence',
    subtitle: 'A diagnostic instrument',
    role: 'Diagnosis',
    blurb:
      'An impasse persisting under refinement is a sign that incoherence has been carried, not solved.',
    body: [
      'Carried incoherence names the pattern by which a field stabilises around an unresolved tension by trading its terms back and forth. Diagnosing it is the entry point to seeing that all four domains are working on the same edge.',
    ],
    related: ['hard-problem', 'measurement-problem', 'libertarian-compatibilist', 'modal-realism-indeterminacy'],
    position: [-6.0, 1.6, 5.5],
  },

  // Derived (2)
  {
    id: 'monadic-inhabitation',
    kind: 'concept',
    group: 'derived',
    index: 'X·01',
    title: 'Monadic Inhabitation',
    subtitle: 'A derived relation',
    role: 'Derived',
    blurb:
      'The relation between a monad and the worldframe it makes the case — neither containment, representation, nor causation.',
    body: [
      'Monadic inhabitation is the structural figure that ties Foundational concepts (monad, worldframe) into the modal account. It is constitutive: there is no worldframe without an inhabitant, and no monad except as inhabiting one.',
    ],
    related: ['monad', 'worldframe', 'modal-domain', 'inhabitation-constitutive', 'paper-inhabitation'],
    position: [-2.0, 2.6, -7.4],
  },
  {
    id: 'trajectory-economy',
    kind: 'concept',
    group: 'derived',
    index: 'X·02',
    title: 'Trajectory Economy',
    subtitle: 'A derived dynamic',
    role: 'Derived',
    blurb:
      'How sequences of inhabited worldframes compose without invoking a global time.',
    body: [
      'A trajectory economy is the way local successions of articulation hang together without requiring a Newtonian backdrop. It supplies the dynamics needed to talk about temporal phenomena — the arrow of time, the directionality of resolution — within the worldframe scheme.',
    ],
    related: ['arrow-of-time', 'wavefunction-resonance', 'r-monad'],
    position: [2.0, 2.6, -7.4],
  },
];

export const CLAIMS: ClaimElement[] = [
  // Foundational (2)
  {
    id: 'freedom-fundamental',
    kind: 'claim',
    status: 'foundational',
    index: 'Cl·01',
    title: 'Freedom is the sole fundamental reality.',
    subtitle: 'Foundational claim',
    blurb:
      'Necessity, possibility, and actuality are derivative modes of how freedom takes hold.',
    body: [
      'The claim is not that everything is "free" in any colloquial sense. It is that any account that takes a determinate state as the explanatory floor will, on examination, be presupposing the openness it then has to derive.',
    ],
    related: ['freedom', 'three-layer-causa-sui', 'paper-freedom'],
    position: [-2.6, 6.6, 1.5],
  },
  {
    id: 'inhabitation-constitutive',
    kind: 'claim',
    status: 'foundational',
    index: 'Cl·02',
    title: 'Worldframes are individuated by their monadic inhabitants.',
    subtitle: 'Foundational claim',
    blurb:
      'There is no worldframe without an inhabitant; identification across worldframes is structurally barred.',
    body: [
      'This is the modal-metaphysical commitment. It refuses Lewisian realism\'s plenum and its ersatzist reductions alike, taking inhabitation itself as primitive.',
    ],
    related: ['worldframe', 'monad', 'monadic-inhabitation', 'paper-worldframes', 'paper-inhabitation'],
    position: [2.6, 6.6, 1.5],
  },

  // Derived (4)
  {
    id: 'disclosive-hard-problem',
    kind: 'claim',
    status: 'derived',
    index: 'Cl·03',
    title: 'The disclosive/structural distinction dissolves the hard problem.',
    subtitle: 'Derived claim',
    blurb:
      'The hard problem is the artefact of treating disclosure as a residue of structural description.',
    body: [
      'Once disclosure is taken as a co-equal register, the explanatory gap is reframed. There is no further fact about consciousness to be derived; the structure already had two registers, and we mistook one of them for the absence of the other.',
    ],
    related: ['disclosive-cut', 'hard-problem', 'marys-room', 'mind-domain', 'paper-disclosive'],
    position: [-5.5, 6.0, -1.4],
  },
  {
    id: 'quantum-worldframe',
    kind: 'claim',
    status: 'derived',
    index: 'Cl·04',
    title: 'The quantum measurement event is a worldframe selection event.',
    subtitle: 'Derived claim',
    blurb:
      'Selection is not collapse-as-process but the worldframe coming-into-coincidence with a candidate articulation.',
    body: [
      'On this reading, the wavefunction is the admissibility profile of a worldframe; what happens at "measurement" is not a stochastic transition but the structural coincidence of monad and articulation.',
    ],
    related: ['wavefunction-resonance', 'measurement-problem', 'quantum-eraser', 'qm-domain', 'paper-inhabitation'],
    position: [5.5, 6.0, -1.4],
  },
  {
    id: 'inhabitation-claim',
    kind: 'claim',
    status: 'derived',
    index: 'Cl·05',
    title: 'Monadic inhabitation is constitutive of worldhood.',
    subtitle: 'Derived claim',
    blurb:
      'There is no fact of the matter about an uninhabited worldframe — not because we cannot know it, but because nothing has been individuated.',
    body: [
      'This is the strong reading: not an epistemic limit but an ontological constitution. The participatory-universe intuitions of Wheeler and others are accommodated without their idealist excesses.',
    ],
    related: ['monadic-inhabitation', 'monad', 'worldframe', 'participatory-universe'],
    position: [-3.0, 5.6, -4.4],
  },
  {
    id: 'agency-causa-sui',
    kind: 'claim',
    status: 'derived',
    index: 'Cl·06',
    title: 'Genuine agency requires the three-layer causa sui structure.',
    subtitle: 'Derived claim',
    blurb:
      'A bounded reflexivity — neither libertarian nor compatibilist — is the structural minimum for origination.',
    body: [
      'The three-layer causa sui escapes the regress that doomed classical agent causation while delivering more than compatibilist responsiveness. It is, on this account, what the free-will impasse has been gesturing at all along.',
    ],
    related: ['three-layer-causa-sui', 'conditions-of-agency', 'libertarian-compatibilist', 'freewill-domain', 'paper-agency'],
    position: [3.0, 5.6, -4.4],
  },
];

export const PHENOMENA: PhenomenonElement[] = [
  // Foundational/Metaphysical (3)
  {
    id: 'hard-problem',
    kind: 'phenomenon',
    domain: 'foundational',
    index: 'P·01',
    title: 'The Hard Problem of Consciousness',
    blurb: 'Why is any of this experienced rather than merely processed?',
    body: [
      'Chalmers\' formulation: any complete functional account leaves a residue — why is there something it is like to be the system? The system here treats this not as a problem to be solved by adding ingredients but as a sign that disclosure has been mistakenly enrolled into the structural register.',
    ],
    related: ['disclosive-cut', 'mind-domain', 'disclosive-hard-problem', 'marys-room'],
    position: [-4.6, -3.5, 5.6],
  },
  {
    id: 'marys-room',
    kind: 'phenomenon',
    domain: 'foundational',
    index: 'P·02',
    title: "Mary's Room",
    blurb: 'Knowledge of all physical facts seems insufficient for knowing what red is like.',
    body: [
      'Jackson\'s thought experiment exposes the same fault line as the hard problem — there is something Mary lacks, even given complete structural knowledge. The disclosive register is what is gained when she leaves the room.',
    ],
    related: ['hard-problem', 'disclosive-cut', 'mind-domain'],
    position: [-2.4, -3.8, 6.4],
  },
  {
    id: 'grounding-regress',
    kind: 'phenomenon',
    domain: 'foundational',
    index: 'P·03',
    title: 'The Grounding Regress',
    blurb: 'Each putative ground appears to require a further ground; nothing terminates the chain.',
    body: [
      'The regress is taken here as evidence that "ground" itself is mis-conceived. Freedom as fundamental is not a further ground but the recognition that the demand for one is what generates the regress.',
    ],
    related: ['freedom', 'freedom-fundamental'],
    position: [0.0, -4.0, 7.0],
  },

  // Quantum Mechanical (2)
  {
    id: 'measurement-problem',
    kind: 'phenomenon',
    domain: 'qm',
    index: 'P·04',
    title: 'The Measurement Problem',
    blurb: 'Unitary evolution does not yield definite outcomes.',
    body: [
      'The Schrödinger equation is unitary; measurement outcomes are definite. Standard interpretations bridge the gap by adding a collapse postulate, branching, or hidden variables. The system reframes selection as a worldframe-articulation coincidence rather than a process.',
    ],
    related: ['qm-domain', 'wavefunction-resonance', 'quantum-worldframe'],
    position: [-7.4, -2.9, 2.8],
  },
  {
    id: 'quantum-eraser',
    kind: 'phenomenon',
    domain: 'qm',
    index: 'P·05',
    title: 'Quantum Eraser & Delayed Choice',
    blurb: 'The articulation under which a system is brought to coincidence appears to be retro-effective.',
    body: [
      'Wheeler\'s delayed-choice and the quantum eraser experiments make it stark that "what happened" depends on the articulation chosen later. On the worldframe reading, this is unsurprising: the articulation is part of the coincidence event, not a description of a prior fact.',
    ],
    related: ['qm-domain', 'measurement-problem', 'wavefunction-resonance', 'participatory-universe'],
    position: [-9.0, -2.9, 0.4],
  },

  // Modal (2)
  {
    id: 'modal-realism-indeterminacy',
    kind: 'phenomenon',
    domain: 'modal',
    index: 'P·06',
    title: "Modal Realism's Indeterminacy",
    blurb: 'No principled fact about which world is the actual one.',
    body: [
      'Lewisian modal realism takes "actual" as indexical, leaving no work for a fact of inhabitation to do. The system rejects the indexical move: a worldframe is the actual one because it is inhabited, and inhabitation is not indexical.',
    ],
    related: ['modal-domain', 'monadic-inhabitation', 'inhabitation-claim'],
    position: [7.4, -2.9, 2.8],
  },
  {
    id: 'participatory-universe',
    kind: 'phenomenon',
    domain: 'modal',
    index: 'P·07',
    title: 'The Participatory Universe',
    blurb: 'Wheeler\'s suggestion that observation is constitutive of physical fact.',
    body: [
      'Wheeler\'s "It from Bit" and the participatory universe gesture at a stronger role for observation than standard QM admits. The system accommodates this without idealism: inhabitation is constitutive of worldhood, but not of any particular phenomenal datum.',
    ],
    related: ['modal-domain', 'inhabitation-claim', 'quantum-eraser', 'monadic-inhabitation'],
    position: [9.0, -2.9, 0.4],
  },

  // Agency (1)
  {
    id: 'libertarian-compatibilist',
    kind: 'phenomenon',
    domain: 'agency',
    index: 'P·08',
    title: 'The Libertarian/Compatibilist Impasse',
    blurb: 'A century of trench warfare around what an agent\'s "origination" could even be.',
    body: [
      'Compatibilism saves moral practice; libertarianism saves origination; neither can explain the other\'s commitments. The system reads this as carried incoherence: a structural answer (the three-layer causa sui) has been sought in the wrong place.',
    ],
    related: ['freewill-domain', 'three-layer-causa-sui', 'conditions-of-agency', 'agency-causa-sui', 'carried-incoherence'],
    position: [4.6, -3.8, 5.6],
  },

  // Temporal (1)
  {
    id: 'arrow-of-time',
    kind: 'phenomenon',
    domain: 'temporal',
    index: 'P·09',
    title: 'The Arrow of Time',
    blurb: 'Asymmetric in lived experience, symmetric in fundamental physics.',
    body: [
      'The directionality of time resists derivation from time-symmetric physical law. Within the trajectory-economy framing, directionality is a structural feature of how worldframe successions hang together, not an extra fact about a substance called "time."',
    ],
    related: ['trajectory-economy', 'wavefunction-resonance'],
    position: [2.4, -4.0, 6.4],
  },
];

export const PAPERS: PaperElement[] = [
  {
    id: 'paper-freedom',
    kind: 'paper',
    status: 'published',
    index: '§ I',
    title: 'Freedom as Ground',
    subtitle: 'On taking openness as the explanatory floor',
    venue: 'Synthese, 2024',
    authors: 'R. van den Broek',
    blurb:
      'Argues that the demand for a ground beneath freedom presupposes the openness it would derive.',
    body: [
      'The paper develops the claim that freedom is fundamental by showing that any candidate "ground beneath" freedom is intelligible only against the openness it would explain. It contrasts this with brute-fact realism and necessitarian alternatives.',
    ],
    related: ['freedom', 'freedom-fundamental', 'grounding-regress'],
    position: [-9.2, 1.4, 4.6],
  },
  {
    id: 'paper-worldframes',
    kind: 'paper',
    status: 'published',
    index: '§ II',
    title: 'Worldframes and the Conditions of Intelligibility',
    subtitle: 'Why modal claims need an inhabitant',
    venue: 'Philosophical Quarterly, 2025',
    authors: 'R. van den Broek',
    blurb:
      'Develops the worldframe concept in contrast with both Lewisian realism and ersatzism.',
    body: [
      'A defence of the worldframe as the proper unit of inhabited reality, proceeding by showing what each major modal-metaphysical alternative cannot give an account of.',
    ],
    related: ['worldframe', 'inhabitation-constitutive', 'conditions-of-intelligibility', 'modal-domain'],
    position: [-9.2, -0.1, 5.4],
  },
  {
    id: 'paper-disclosive',
    kind: 'paper',
    status: 'review',
    index: '§ III',
    title: 'The Disclosive Cut',
    subtitle: 'Phenomenal/structural without dualism',
    venue: 'Mind (under review)',
    authors: 'R. van den Broek',
    blurb:
      'A reframing of the hard problem in terms of the disclosive/structural distinction.',
    body: [
      'The paper articulates the cut between disclosure and structure, and argues that the hard problem disappears once the cut is recognised as a distinction between registers rather than a gap between levels.',
    ],
    related: ['disclosive-cut', 'disclosive-hard-problem', 'hard-problem', 'mind-domain'],
    position: [9.2, 1.4, 4.6],
  },
  {
    id: 'paper-inhabitation',
    kind: 'paper',
    status: 'review',
    index: '§ IV',
    title: 'Monadic Inhabitation and Quantum Selection',
    subtitle: 'One structure, two manifestations',
    venue: 'Foundations of Physics (drafted)',
    authors: 'R. van den Broek',
    blurb:
      'Argues that inhabitation in modal metaphysics and selection in QM are aspects of one structural feature.',
    body: [
      'The paper builds the bridge that the wider system depends on: that what looks like two distinct philosophical problems are aspects of a single underlying structural feature, expressed through the R-monad apparatus.',
    ],
    related: ['monadic-inhabitation', 'quantum-worldframe', 'wavefunction-resonance', 'inhabitation-constitutive'],
    position: [9.2, -0.1, 5.4],
  },
  {
    id: 'paper-modal-resonance',
    kind: 'paper',
    status: 'progress',
    index: '§ V',
    title: 'Modal Resonance',
    subtitle: 'Reinterpreting the wavefunction',
    venue: 'In progress',
    authors: 'R. van den Broek',
    blurb:
      'Develops the formal apparatus for reading Ψ as an admissibility profile.',
    body: [
      'A more technical companion to "Monadic Inhabitation and Quantum Selection," developing the coherence-functional formalism in detail and addressing the relationship to standard QM formalism.',
    ],
    related: ['wavefunction-resonance', 'coherence-functional', 'r-monad', 'qm-domain'],
    position: [-9.2, -1.7, 5.6],
  },
  {
    id: 'paper-agency',
    kind: 'paper',
    status: 'planned',
    index: '§ VI',
    title: 'Agency, Causa Sui, and the Conditions of Freedom',
    subtitle: 'A structural account of origination',
    venue: 'Planned',
    blurb:
      'Will develop the three-layer causa sui as the structural minimum for genuine agency.',
    body: [
      'The paper will articulate the three-layer causa sui, contrast it with classical agent causation and contemporary compatibilism, and show how it follows from the conditions of agency rather than being added to them.',
    ],
    related: ['three-layer-causa-sui', 'conditions-of-agency', 'agency-causa-sui', 'freewill-domain'],
    position: [9.2, -1.7, 5.6],
  },
  {
    id: 'paper-synthesis',
    kind: 'paper',
    status: 'planned',
    index: '§ VII',
    title: 'The Structural Feature',
    subtitle: 'A bounded synthesis',
    venue: 'Planned · monograph',
    blurb:
      'The book-length synthesis tying the four domains to the single underlying feature.',
    body: [
      'The synthesis will argue, at length, that the structural feature is real philosophical work — neither a metaphor across domains nor a unification by stipulation, but the locus at which the regresses of four fields come to rest in the same way.',
    ],
    related: ['feature', 'freedom', 'worldframe', 'monad', 'disclosive-cut'],
    position: [0.0, -5.6, -3.0],
  },
];

export const REGION_LABELS: RegionLabel[] = [
  { id: 'r-feature', text: 'The Unifying Feature', roman: '∗', position: [0, -1.5, 0.0] },
  { id: 'r-foundational', text: 'Foundational Concepts', roman: 'I', position: [0, 5.7, -3.6] },
  { id: 'r-formal', text: 'Formal Apparatus', roman: 'II', position: [8.2, 3.6, 1.6] },
  { id: 'r-diagnostic', text: 'Diagnostic Concepts', roman: 'III', position: [-8.2, 3.6, 1.6] },
  { id: 'r-derived', text: 'Derived Concepts', roman: 'IV', position: [0, 3.6, -7.4] },
  { id: 'r-claims', text: 'Claims of the System', roman: 'V', position: [0, 7.7, 1.5] },
  { id: 'r-phenomena', text: 'Phenomena Addressed', roman: 'VI', position: [0, -4.9, 7.0] },
  { id: 'r-papers-w', text: 'Papers — Published & Drafted', roman: 'VII', position: [-9.2, 2.6, 4.6] },
  { id: 'r-papers-e', text: 'Papers — Under Review & Planned', roman: 'VIII', position: [9.2, 2.6, 4.6] },
];

export const ALL_ELEMENTS: SystemElement[] = [
  FEATURE,
  ...DOMAINS,
  ...CONCEPTS,
  ...CLAIMS,
  ...PHENOMENA,
  ...PAPERS,
];

export const BY_ID: Record<string, SystemElement> = Object.fromEntries(
  ALL_ELEMENTS.map((el) => [el.id, el]),
);
